import { Alert } from 'antd';
import { useCliente } from '../api/useClientes';
import { usePagosList } from '../../pagos/api/usePagos';
import { useT } from '../../../locales';
import { formatearPeriodo } from '../../../lib/date';
import { estadoCoberturaPrepago } from '../cliente-cobertura';

interface ClienteCoberturaPrepagoBannerProps {
  clienteId: number;
}

/**
 * S4.3 (T13/T27) + S6.2/S6.4 (T37): explica por qué un cliente prepago puede
 * no tener facturas del período. La fuente primaria es el estado del paquete
 * prepago que viaja en el detalle del cliente (GET /clientes/:id): un paquete
 * CANCELADO o CONSUMIDO no muestra cobertura aunque su pago siga VIGENTE.
 *
 * S6.2: la lectura de pagos (GET /clientes/:id/pagos) es SOLO el fallback
 * legacy — paquete sin el snapshot b02e536 o detalle sin paquete — y se pide
 * únicamente cuando hace falta, para que un operador sin permiso de pagos
 * vea la cobertura en vez de un 403 silencioso. Si ese fallback sí dispara y
 * la lectura falla, el banner lo avisa en pantalla. Los errores del detalle
 * degradan a "sin aviso" (nunca tumban la lista de facturas). El detalle ya lo
 * consulta la página (misma query key): React Query reutiliza el cache, sin
 * fetch extra.
 */
export function ClienteCoberturaPrepagoBanner({ clienteId }: ClienteCoberturaPrepagoBannerProps) {
  const t = useT();
  const { data: cliente } = useCliente(clienteId);
  const paquete = cliente?.coberturaPrepago;
  // El campo del detalle responde la cobertura por sí solo; sin él (o con un
  // paquete activo sin snapshot) recién ahí se justifica la lectura de pagos.
  const necesitaFallbackPagos =
    cliente !== undefined &&
    (paquete == null || (paquete.estado === 'ACTIVO' && !paquete.tieneSnapshot));
  const pagos = usePagosList(
    { idCliente: clienteId, limit: 100, offset: 0 },
    { enabled: necesitaFallbackPagos },
  );
  const estado = estadoCoberturaPrepago(paquete, pagos.data?.data ?? []);

  if (estado.tipo === 'PAQUETE_INVALIDO') {
    return (
      <Alert
        type="warning"
        showIcon
        title={t('pages.clientes.coberturaPrepago.invalidaTitulo')}
        description={t('pages.clientes.coberturaPrepago.invalidaDescripcion', {
          idPaquete: estado.idPaquete,
        })}
      />
    );
  }

  if (estado.tipo === 'COBERTURA') {
    const periodo = formatearPeriodo(estado.cobertura.inicio, estado.cobertura.fin);
    if (!periodo) return null;
    return (
      <Alert
        type="info"
        showIcon
        title={t('pages.clientes.coberturaPrepago.title')}
        description={t('pages.clientes.coberturaPrepago.descripcion', { periodo })}
      />
    );
  }

  if (necesitaFallbackPagos && pagos.isError) {
    return (
      <Alert
        type="warning"
        showIcon
        title={t('pages.clientes.coberturaPrepago.pagosErrorTitulo')}
        description={t('pages.clientes.coberturaPrepago.pagosErrorDescripcion')}
      />
    );
  }

  return null;
}
